import React, { useState, useMemo } from 'react';
import {
    Box, Alert, Snackbar,
    TextField, InputAdornment,
    Typography, Button, ToggleButton, ToggleButtonGroup,
    Autocomplete, IconButton, Tooltip
} from '@mui/material';
import BurbujasLoader from '../../components/UIElements/BurbujasLoader';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import useEventoYCurso from './hooks/useEventoYCurso';
import EventoYCursoTable from './components/EventoYCursoTable';
import EventoYCursoModal from './components/EventoYCursoModal';
import ModalCrearCurso from '../../components/NotaDeAutorizacion/Modals/ModalCrearCurso';

const GestionEventoYCurso = () => {
    const {
        data, loading, error,
        updateItem, changeEstado,
        refreshData, auxiliaryData
    } = useEventoYCurso();

    const [modalOpen, setModalOpen] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('todos');
    const [selectedMinisterios, setSelectedMinisterios] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [crearCursoModalOpen, setCrearCursoModalOpen] = useState(false);

    // Opciones de ministerios (desde auxiliaryData o derivados de areas)
    const ministeriosOptions = useMemo(() => {
        if (auxiliaryData.ministerios && auxiliaryData.ministerios.length > 0) {
            return auxiliaryData.ministerios;
        }
        const map = new Map();
        auxiliaryData.areas?.forEach(a => {
            if (a.detalle_ministerio?.cod) {
                map.set(a.detalle_ministerio.cod, a.detalle_ministerio);
            } else if (a.ministerio) {
                map.set(a.ministerio, { cod: a.ministerio, nombre: a.ministerio });
            }
        });
        return Array.from(map.values());
    }, [auxiliaryData.ministerios, auxiliaryData.areas]);

    // Opciones de áreas disponibles (filtradas opcionalmente si hay ministerios seleccionados)
    const availableAreas = useMemo(() => {
        const allAreas = auxiliaryData.areas || [];
        if (!selectedMinisterios || selectedMinisterios.length === 0) {
            return allAreas;
        }
        const minCods = selectedMinisterios.map(m => m.cod);
        return allAreas.filter(a => minCods.includes(a.ministerio) || minCods.includes(a.detalle_ministerio?.cod));
    }, [auxiliaryData.areas, selectedMinisterios]);

    const handleMinisteriosChange = (event, newValue) => {
        setSelectedMinisterios(newValue);
        if (newValue.length > 0) {
            const minCods = newValue.map(m => m.cod);
            setSelectedAreas(prev => prev.filter(a => minCods.includes(a.ministerio) || minCods.includes(a.detalle_ministerio?.cod)));
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedMinisterios([]);
        setSelectedAreas([]);
        setFilter('todos');
    };

    const hasActiveFilters = Boolean(
        searchTerm ||
        selectedMinisterios.length > 0 ||
        selectedAreas.length > 0 ||
        filter !== 'todos'
    );

    const filteredData = useMemo(() => {
        let result = data;

        // 1. Filtrar por estado de curso (AND)
        if (filter !== 'todos') {
            result = result.filter(item => item.estado === filter);
        }

        // 2. Filtrar por búsqueda de texto (nombre del curso, código corto del curso y nro de evento) (AND)
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase().trim();
            result = result.filter(item => {
                const codMatch = Boolean(item.cod && item.cod.toLowerCase().includes(lowerSearch));
                const nombreMatch = Boolean(item.nombre && item.nombre.toLowerCase().includes(lowerSearch));
                const numeroEventoMatch = Boolean(item.numero_evento != null && String(item.numero_evento).toLowerCase().includes(lowerSearch));
                const presentacionMatch = Boolean(item.detalle_evento?.presentacion && item.detalle_evento.presentacion.toLowerCase().includes(lowerSearch));
                return codMatch || nombreMatch || numeroEventoMatch || presentacionMatch;
            });
        }

        // 3. Filtrar por ministerio (AND)
        if (selectedMinisterios && selectedMinisterios.length > 0) {
            const minCods = selectedMinisterios.map(m => m.cod);
            result = result.filter(item => {
                const itemMinCod = item.detalle_area?.ministerio ||
                                   item.detalle_area?.detalle_ministerio?.cod ||
                                   auxiliaryData.areas?.find(a => a.cod === item.area)?.ministerio;
                return itemMinCod && minCods.includes(itemMinCod);
            });
        }

        // 4. Filtrar por área (AND)
        if (selectedAreas && selectedAreas.length > 0) {
            const areaCods = selectedAreas.map(a => a.cod);
            result = result.filter(item => {
                const itemAreaCod = item.area || item.codArea || item.detalle_area?.cod;
                return itemAreaCod && areaCods.includes(itemAreaCod);
            });
        }

        return result;
    }, [data, searchTerm, filter, selectedMinisterios, selectedAreas, auxiliaryData.areas]);

    const handleEdit = (record) => {
        setCurrentRecord(record);
        setModalOpen(true);
    };



    const handleSave = async (formData) => {
        const result = await updateItem(formData);

        if (result.success) {
            const message = formData.tieneEvento
                ? 'Evento y curso actualizados correctamente'
                : (formData.perfil && formData.area_tematica && formData.tipo_certificacion)
                    ? 'Evento creado y curso actualizado correctamente'
                    : 'Curso actualizado correctamente';

            setNotification({
                open: true,
                message,
                severity: 'success'
            });
            setModalOpen(false);
        } else {
            setNotification({ open: true, message: result.error, severity: 'error' });
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const pendientesCount = useMemo(() => {
        return data.filter(item => item.estado === 'PVICT').length;
    }, [data]);

    if (loading && data.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><BurbujasLoader /></Box>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h5" gutterBottom>
                Gestión de Eventos y Cursos
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Barra superior: Estados y botón Nuevo Curso */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(e, newFilter) => {
                        if (newFilter !== null) setFilter(newFilter);
                    }}
                    size="small"
                    color="primary"
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& .MuiToggleButtonGroup-grouped': {
                            border: '1px solid #e0e0e0',
                        }
                    }}
                >
                    <ToggleButton value="todos" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>Todos</ToggleButton>
                    <ToggleButton value="AUT" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>Autorizados</ToggleButton>
                    <ToggleButton value="MAQ" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>Maquetados</ToggleButton>
                    <ToggleButton value="CON" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>Configurados</ToggleButton>
                    <ToggleButton
                        value="PVICT"
                        color="primary"
                        sx={{
                            px: 2,
                            textTransform: 'none',
                            fontWeight: pendientesCount > 0 ? 700 : 500,
                            ...(pendientesCount > 0 && {
                                color: 'primary.main',
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                }
                            })
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Pendientes Cargar en Victorius
                            {pendientesCount > 0 && (
                                <Box
                                    sx={{
                                        backgroundColor: 'error.main',
                                        color: 'white',
                                        borderRadius: '12px',
                                        px: 1,
                                        py: 0.3,
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {pendientesCount}
                                </Box>
                            )}
                        </Box>
                    </ToggleButton>
                    <ToggleButton value="EC" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>Creados en Victorius</ToggleButton>
                    <ToggleButton value="NVIG" sx={{ px: 2, textTransform: 'none', fontWeight: 500 }}>No vigentes</ToggleButton>
                </ToggleButtonGroup>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setCrearCursoModalOpen(true)}
                    sx={{ borderRadius: '20px', px: 3, textTransform: 'none', fontWeight: 600 }}
                >
                    Nuevo Curso
                </Button>
            </Box>

            {/* Barra de filtros: Búsqueda, Ministerio y Área */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Buscar por código, nombre o nro de evento..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: 1.5, minWidth: 280 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Autocomplete
                    multiple
                    size="small"
                    limitTags={1}
                    options={ministeriosOptions}
                    getOptionLabel={(option) => option.nombre || option.cod || ''}
                    isOptionEqualToValue={(option, val) => option.cod === val.cod}
                    value={selectedMinisterios}
                    onChange={handleMinisteriosChange}
                    renderInput={(params) => (
                        <TextField {...params} label="Ministerio" placeholder={selectedMinisterios.length === 0 ? "Todos los ministerios" : ""} />
                    )}
                    sx={{ flex: 1, minWidth: 230 }}
                />

                <Autocomplete
                    multiple
                    size="small"
                    limitTags={1}
                    options={availableAreas}
                    getOptionLabel={(option) => option.nombre || option.cod || ''}
                    isOptionEqualToValue={(option, val) => option.cod === val.cod}
                    value={selectedAreas}
                    onChange={(event, newValue) => setSelectedAreas(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} label="Área" placeholder={selectedAreas.length === 0 ? "Todas las áreas" : ""} />
                    )}
                    sx={{ flex: 1, minWidth: 230 }}
                />

                {hasActiveFilters && (
                    <Tooltip title="Limpiar todos los filtros">
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<FilterAltOffIcon />}
                            onClick={handleClearFilters}
                            sx={{ textTransform: 'none', height: 40, px: 2, whiteSpace: 'nowrap' }}
                        >
                            Limpiar filtros
                        </Button>
                    </Tooltip>
                )}
            </Box>

            <EventoYCursoTable
                data={filteredData}
                onEdit={handleEdit}
            />

            <EventoYCursoModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                onChangeEstado={changeEstado}
                record={currentRecord}
                auxiliaryData={auxiliaryData}
                filter={filter}
            />

            <ModalCrearCurso
                open={crearCursoModalOpen}
                onClose={() => setCrearCursoModalOpen(false)}
                onSuccess={(msg) => {
                    setNotification({ open: true, message: msg, severity: 'success' });
                    refreshData();
                }}
                areas={auxiliaryData.areas}
            />



            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GestionEventoYCurso;
